import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { GearbeltEvent } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { businessSelectorDefaultAccordionPanels, IBusinessAttribute, SelectorTypes, VerticalPriorityMap } from '@site-mate/sitemate-global-shared';
import clsx from 'clsx';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import { match } from 'ts-pattern';

import CheckIcon from '@/components/icons/check';
import CheckCircleIcon from '@/components/icons/check-circle';
import LoadingIcon from '@/components/icons/loading';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Form, FormFieldItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { DashpivotApiService } from '@/services/dashpivot/dashpivot-api';
import { useEvents } from '@/utils/events';

import { businessAttributeOtherOption, businessAttributesPlaceholders, SignUpStepProps } from '../signup-form-utils';

export function BusinessAttributesSelectorStep<T extends FieldValues>({ form, register, onNextStep }: SignUpStepProps<T>) {
  const [kindsOnDisplay, setKindsOnDisplay] = useState([SelectorTypes.Vertical, SelectorTypes.Role]);

  const [businessAttributesOptionsList, setBusinessAttributesOptionsList] = useState({
    [SelectorTypes.Vertical]: [] as IBusinessAttribute[],
    [SelectorTypes.Role]: [] as IBusinessAttribute[],
    [SelectorTypes.Discipline]: [] as IBusinessAttribute[],
  });
  const [allDisciplines, setAllDisciplines] = useState([] as IBusinessAttribute[]);
  const [selectedBusinessAttributes, setSelectedBusinessAttributes] = useState({
    [SelectorTypes.Vertical]: {} as IBusinessAttribute,
    [SelectorTypes.Role]: {} as IBusinessAttribute,
    [SelectorTypes.Discipline]: {} as IBusinessAttribute,
  });

  const [activeKind, setActiveKind] = useState<SelectorTypes | string>(SelectorTypes.Vertical);
  const [isOtherInputTouched, setIsOtherInputTouched] = useState(false);
  const [showCheck, setShowCheck] = useState({ [kindsOnDisplay[0]]: false, [kindsOnDisplay[1]]: false });
  const [hasNextButtonBeenClicked, setHasNextButtonBeenClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useEvents();

  useEffect(() => {
    loadBusinessAttributes();
  }, []);

  useEffect(() => {
    kindsOnDisplay.forEach((kind) => {
      if (selectedBusinessAttributes[kind]?.code === businessAttributeOtherOption.code) {
        scrollIntoView();
      }
    });
  }, [selectedBusinessAttributes, kindsOnDisplay]);

  const scrollIntoView = () => {
    const otherInput = document.getElementById('other-input');
    otherInput?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const loadBusinessAttributes = async () => {
    const [verticals, roles, disciplines] = await Promise.all([
      await (await DashpivotApiService.getVerticals()).json(),
      await (await DashpivotApiService.getRoles()).json(),
      await (await DashpivotApiService.getDisciplines()).json(),
    ]);
    const allVerticals = loadVerticals(verticals);
    const allRoles = roles ? [...roles, businessAttributeOtherOption as IBusinessAttribute] : [];

    setBusinessAttributesOptionsList((prevState) => {
      return { ...prevState, [SelectorTypes.Vertical]: allVerticals, [SelectorTypes.Role]: allRoles };
    });

    setAllDisciplines(() => {
      return disciplines ?? [];
    });
  };

  useEffect(() => {
    loadRelevantDisciplines();
    if (selectedBusinessAttributes[SelectorTypes.Vertical].name) {
      setActiveKind(kindsOnDisplay[1]);
    }
  }, [selectedBusinessAttributes[SelectorTypes.Vertical].code, kindsOnDisplay[1]]);

  const loadVerticals = (verticals: IBusinessAttribute[]) => {
    const updatedVerticals = verticals?.map((vertical) => {
      vertical.name = vertical.description as string;
      return vertical;
    });
    const sortedVerticals = sortVerticals(updatedVerticals ?? []);
    return [...sortedVerticals, businessAttributeOtherOption as IBusinessAttribute];
  };

  const sortVerticals = (verticals: IBusinessAttribute[]): IBusinessAttribute[] => {
    return verticals.sort((a, b) => {
      const priorityA = VerticalPriorityMap.get(a.name) ?? Infinity;
      const priorityB = VerticalPriorityMap.get(b.name) ?? Infinity;

      return priorityA - priorityB || a.code.localeCompare(b.code);
    });
  };

  const loadRelevantDisciplines = () => {
    const selectedVerticalCode = selectedBusinessAttributes[SelectorTypes.Vertical].code;
    if (!selectedVerticalCode) return;
    const relevantDisciplines = allDisciplines.filter((discipline) => discipline.linkedAttributeCodes?.includes(selectedVerticalCode));

    if (relevantDisciplines.length) {
      setBusinessAttributesOptionsList((prevOptions) => {
        return {
          ...prevOptions,
          [SelectorTypes.Discipline]: [...relevantDisciplines, businessAttributeOtherOption as IBusinessAttribute],
        };
      });
      setKindsOnDisplay([SelectorTypes.Vertical, SelectorTypes.Discipline]);
    } else if (kindsOnDisplay[1] === SelectorTypes.Discipline) {
      setKindsOnDisplay([SelectorTypes.Vertical, SelectorTypes.Role]);
    }
  };

  const showRequiredError = useMemo(() => {
    const errorMap: Record<SelectorTypes, boolean> = {} as Record<SelectorTypes, boolean>;
    kindsOnDisplay.forEach((kind) => {
      errorMap[kind] = !selectedBusinessAttributes[kind].name && hasNextButtonBeenClicked;
    });
    return errorMap;
  }, [selectedBusinessAttributes, hasNextButtonBeenClicked]);

  const onCustomBusinessAttributeConfirmed = (kind: SelectorTypes) => {
    // Send events for tracking
    match(kind)
      .with(SelectorTypes.Vertical, () => {
        trackEvent(GearbeltEvent.SignupVerticalManuallyEntered, {
          Context: selectedBusinessAttributes[kind].name,
        });
      })
      .with(SelectorTypes.Role, () => {
        trackEvent(GearbeltEvent.SignupRoleManuallyEntered, {
          Context: selectedBusinessAttributes[kind].name,
        });
      })
      .with(SelectorTypes.Discipline, () => {
        // Unhandled for now
      })
      .exhaustive();
  };

  const onBusinessAttributeSelection = (kind: SelectorTypes, option: IBusinessAttribute, value: string) => {
    setIsOtherInputTouched(false);
    if (value) {
      setSelectedBusinessAttributes({
        ...selectedBusinessAttributes,
        [kind]: option,
      });
      handleActiveKindChange(kind);
      setShowCheck({ ...showCheck, [kind]: true });

      // Send events for tracking
      match(kind)
        .with(SelectorTypes.Vertical, () => {
          trackEvent(GearbeltEvent.SignupVerticalSelected, {
            Context: option.code,
          });
        })
        .with(SelectorTypes.Role, () => {
          trackEvent(GearbeltEvent.SignupRoleSelected, {
            Context: option.code,
          });
        })
        .with(SelectorTypes.Discipline, () => {
          // Unhandled for now
        })
        .exhaustive();
    } else {
      setSelectedBusinessAttributes({
        ...selectedBusinessAttributes,
        [kind]: { ...businessAttributeOtherOption, name: '' },
      });
      setShowCheck({ ...showCheck, [kind]: false });
    }
  };

  const handleActiveKindChange = (kind: SelectorTypes) => {
    if (kind === SelectorTypes.Vertical) {
      setActiveKind(kindsOnDisplay[1]);
    } else {
      setActiveKind('');
    }
  };

  const isNextButtonDisabled = useMemo(() => {
    return (
      isLoading ||
      (hasNextButtonBeenClicked &&
        Object.entries(showCheck)
          .filter(([kind]) => kindsOnDisplay.includes(kind as SelectorTypes))
          .some(([kind, value]) => {
            return !value || !selectedBusinessAttributes[kind as SelectorTypes].name;
          }))
    );
  }, [isLoading, hasNextButtonBeenClicked, showCheck, selectedBusinessAttributes, kindsOnDisplay]);

  const handleNextButtonClick = () => {
    setHasNextButtonBeenClicked(true);
    if (
      Object.entries(showCheck)
        .filter((entry) => kindsOnDisplay.includes(entry[0] as SelectorTypes))
        .every((entry) => {
          return entry[1] && selectedBusinessAttributes[entry[0] as SelectorTypes].name;
        })
    ) {
      const updatedOtherFormValues = Object.entries(form.getValues().businessAttributes).reduce(
        (acc, [kind, value]) => {
          acc[kind as SelectorTypes] = kindsOnDisplay.includes(kind as SelectorTypes) && value ? selectedBusinessAttributes[kind as SelectorTypes] : null;
          return acc;
        },
        {} as T['businessAttributes'],
      );
      form.setValue(`businessAttributes` as Path<T>, updatedOtherFormValues);
      onNextStep?.();
    } else {
      setIsLoading(false);
    }
  };

  const { control } = form;

  return (
    <div className="z-10 flex w-fit max-w-[900px] flex-col gap-[24px] rounded bg-white p-11 shadow">
      <h2 className="text-xl font-bold">Let us know more about what you do</h2>
      <p className="text-default-text text-sm">
        Tell us a little about your company, so we can recommend templates and features that will be most useful to you and your team. You can change this
        selection later.
      </p>
      <div>
        <Accordion type="single" value={activeKind} onValueChange={setActiveKind} className="border-grey-3 rounded border" collapsible>
          {kindsOnDisplay.map((kind, index) => (
            <AccordionItem value={kind} key={kind} disabled={index !== 0 && !showCheck[kindsOnDisplay[index - 1]]}>
              <AccordionTrigger
                className={clsx(
                  'bg-grey-1 text-default-text hover:bg-grey-2 flex w-full items-center justify-between gap-1 rounded-none px-4 py-2 text-sm data-[disabled]:opacity-50 sm:h-[42px]',
                  index !== 0 && 'border-grey-3 border-t',
                )}>
                <span className="flex items-center justify-start font-bold">
                  {`Step ${index + 1} - ${businessSelectorDefaultAccordionPanels[index].header}`}
                  <span className="pl-1 font-normal">{selectedBusinessAttributes[kind].name}</span>
                  {showRequiredError[kind] && <span className="text-default-red text-sm font-normal">Required</span>}
                </span>

                {selectedBusinessAttributes[kind].name && showCheck[kind] && <CheckCircleIcon className="text-default-green h-6 w-6" />}
              </AccordionTrigger>

              <AccordionContent>
                <Form {...form}>
                  <form className="space-between flex flex-col gap-4">
                    <FormFieldItem control={control} name={`businessAttributes.${kind}` as Path<T>} className="gap-0">
                      {({ onChange }) => {
                        const { onChange: registerOnChange } = register(`businessAttributes.${kind}` as Path<T>);

                        return (
                          <div
                            className={clsx(kind !== SelectorTypes.Role && 'grid-cols-2 md:grid', 'min-h-25 overflow-y-scroll px-4 py-3')}
                            style={{ maxHeight: 'calc(85vh - 350px)' }}>
                            {businessAttributesOptionsList[kind].map((option: IBusinessAttribute) => (
                              <label
                                className="py-1.4 hover:bg-grey-1 flex w-full items-center gap-2 rounded px-2 text-center text-sm font-medium"
                                key={option.code}>
                                <Input
                                  containerClassName="w-fit w-3 h-3"
                                  type="radio"
                                  className="max-h-3 max-w-3"
                                  value={option._id as string}
                                  checked={selectedBusinessAttributes[kind].code === option.code}
                                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const {
                                      target: { value },
                                    } = e;
                                    onChange(value as PathValue<T, Path<T>>);
                                    registerOnChange(e);
                                    onBusinessAttributeSelection(kind, option, value);
                                  }}
                                />
                                <span>{option.name}</span>
                                {kind === SelectorTypes.Role && option.code !== businessAttributeOtherOption.code && (
                                  <span className="text-secondary-text font-normal">- {option.description}</span>
                                )}
                              </label>
                            ))}
                            <div></div>
                            {selectedBusinessAttributes[kind].code === businessAttributeOtherOption.code && (
                              <div className="flex flex-col justify-start">
                                <div id="other-input" className="flex max-w-[336px] justify-start gap-0">
                                  <Input
                                    className={clsx('h-9 w-72 rounded-r-none', !selectedBusinessAttributes[kind] && 'border-r-0')}
                                    placeholder={businessAttributesPlaceholders[kind]}
                                    value={selectedBusinessAttributes[kind].name as string}
                                    error={
                                      (!selectedBusinessAttributes[kind].name && isOtherInputTouched) || (showRequiredError[kind] && hasNextButtonBeenClicked)
                                    }
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                      registerOnChange({ target: { value: null } });
                                      onChange(null as PathValue<T, Path<T>>);
                                      setSelectedBusinessAttributes({
                                        ...selectedBusinessAttributes,
                                        [kind]: { ...businessAttributeOtherOption, name: e.target.value },
                                      });
                                      setIsOtherInputTouched(true);
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    type="button"
                                    aria-label="Confirm"
                                    className={clsx(
                                      'w-full rounded-l-none border-l-0',
                                      !selectedBusinessAttributes[kind]?.name || !isOtherInputTouched ? 'bg-grey-5' : 'bg-default-green',
                                    )}
                                    disabled={!selectedBusinessAttributes[kind].name || !isOtherInputTouched}
                                    onClick={() => {
                                      handleActiveKindChange(kind);
                                      setShowCheck({ ...showCheck, [kind]: true });
                                      onCustomBusinessAttributeConfirmed(kind);
                                    }}>
                                    <CheckIcon className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                                {((!selectedBusinessAttributes[kind].name && isOtherInputTouched) || (showRequiredError[kind] && hasNextButtonBeenClicked)) && (
                                  <p className="text-default-red text-sm">Required</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }}
                    </FormFieldItem>
                  </form>
                </Form>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
      <Button
        disabled={isNextButtonDisabled}
        className="m-auto w-72 py-[16px]"
        onClick={() => {
          setIsLoading(true);
          handleNextButtonClick();
          trackEvent(GearbeltEvent.CTAClicked, {
            Context: 'Vertical and Role modal',
          });
        }}>
        <span className="pr-2">Next</span>
        {isLoading && <LoadingIcon size={16} />}
      </Button>
    </div>
  );
}
